import React, { useState } from 'react';
import { useMount } from '@shared/utils/hooks/useMount';
import { useHotelBookingGuestSiteTelemetry } from '../../HotelBooking.telemetry';
import { useHotelBookingRedirectHealth, wrapWithHotelBookingRedirectProvider } from '../../HotelBooking.health';
import { useEventDisplayName } from '@shared/utils/eventInfo/useEventDisplayName';
import { useHotelSearchConfig } from './useHotelSearchConfig';
import { useQueryParamHelper } from '@shared/core/queryString';
import { HONEYMOON_TAG, HotelSearchContextParams, STAYS_EVENT_HANDLE } from '@shared/constants/accommodations';
import { usePrioritizeTagsQueryParam } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationsConsoleSearchParamsState/usePrioritizeTagsQueryParam';

// Sync import: bundled into this chunk to avoid a double lazy-load waterfall.
// See TRAV-2638 for performance analysis.
import { OtaAccommodationsConsole } from '@apps/stays/AccommodationsConsole/scenarios/ota/OtaAccommodationsConsole';
// Sync for the same reason (TRAV-2638): it is a thin scenario config over the console already in
// this chunk, and lazy-loading it would add a second request on the marketing landing path.
import { HoneymoonAccommodationsConsole } from '@apps/stays/AccommodationsConsole/scenarios/honeymoon/HoneymoonAccommodationsConsole';

export const OtaHotelSearch = wrapWithHotelBookingRedirectProvider(({ eventHandle }: { eventHandle: string }) => {
  const { onLoadComplete } = useHotelBookingRedirectHealth();
  const telemetry = useHotelBookingGuestSiteTelemetry();
  const eventDisplayName = useEventDisplayName();
  const queryParamHelper = useQueryParamHelper();
  const prioritizeTags = usePrioritizeTagsQueryParam();

  const { layout } = useHotelSearchConfig();

  const selectedHotelId = queryParamHelper.getValueString(HotelSearchContextParams.selectedHotel) || undefined;

  /**
   * A link that arrives asking for honeymoon stays gets the honeymoon search rather than the
   * generic one: the tag facet to clear it with, no sponsored placements, no cars promo, and
   * `scenario: 'honeymoon'` on the console's events. This is how public honeymoon search runs under
   * the generic `stays` event (`/stays/hotel/search?prioritizeTag=honeymoon`).
   *
   * Only on the `stays` event. Every other event's OTA search keeps its own console even when a link
   * carries the tag (a tag-configured placement on a couple's site): there the tag only filters.
   *
   * Read once, on arrival. The facet writes the tag in and out of the URL, so deciding on every
   * render would swap consoles — and drop the guest's search — the moment they unchecked it. A guest
   * who unchecks it and then reloads gets the generic console, since the tag is no longer in the URL.
   */
  const [isHoneymoonSearch] = useState(() => eventHandle === STAYS_EVENT_HANDLE && prioritizeTags.includes(HONEYMOON_TAG));

  useMount(() => {
    onLoadComplete();
    telemetry.hotelSearch.enter();
  });

  const Console = isHoneymoonSearch ? HoneymoonAccommodationsConsole : OtaAccommodationsConsole;

  return (
    <Console
      eventHandle={eventHandle}
      eventName={eventDisplayName}
      selectedHotelId={selectedHotelId}
      layout={layout}
    />
  );
});
